// @owners { team: patient-health }
import { PressableAltoIcon, Row, SmPadding, XxsSpacing } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { InputIcon, Text, palette } from '@alto/pocky';
import { faSearch } from '@fortawesome/pro-light-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { push } from 'react-router-redux';
import styled from 'styled-components';
import { changeModalStep, openModal } from '~shared/actions/modal';
import { getInstructionalVideosForMedication } from '~shared/features/my-meds/helper';
import { getMedicationsFromPrescriptions } from '~shared/features/my-meds/selectors/getMedicationsFromPrescriptions';
import { type Medication } from '~shared/features/my-meds/types';
import { getPrescriptions } from '~shared/features/prescriptions/selectors/getPrescriptions';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { useAnalytics } from '~shared/hooks';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { generateSendPrescriptionOptionsViewedEvent } from '~shared/lib/analytics/src/events/getSendPrescriptionOptionsViewed';
import { DESTINATION_NAMES, generateSupportViewedEvent } from '~shared/lib/analytics/src/events/getSupportViewed';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import MyMedsEmpty from './MyMedsEmpty';
import { MedList, MedListSearchResults } from './med-list';
import { ContentWrapper, Main, PageBackground, Sidebar } from '~web/components/Page';
import PageHeader from '~web/components/PageHeader';

type Props = {
  readonly params: {
    rxNumber?: string | null;
  };
};

const HeaderRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const MedSearch = styled(InputIcon)`
  width: 35%;
`;

const CenteredText = styled(Text)`
  text-align: center;
`;

export const MyMeds = ({ params: { rxNumber } }: Props) => {
  const { trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const [searchTerm, setSearchTerm] = useState('');
  const medicationsFromPrescriptions = useSelectorShared(getMedicationsFromPrescriptions);
  const onboarding = useSelectorShared((state) => state.auth.onboarding);
  const prescriptions = useSelectorShared(getPrescriptions);
  const transfers = useSelectorShared((state) => state.prescriptions.transfers);
  const user = useSelectorShared(getCurrentUser);

  const openSendPrescriptionOptions = useCallback(() => {
    dispatch(sendAnalyticsEvent(generateSendPrescriptionOptionsViewedEvent(ORIGIN_NAMES.MED_LIST)));
    dispatch(push('me/send-prescription-options'));
  }, [dispatch]);

  const openFAQs = useCallback(() => {
    dispatch(sendAnalyticsEvent(generateSupportViewedEvent(ORIGIN_NAMES.MED_LIST, DESTINATION_NAMES.FAQ)));
    dispatch(push('/me/faq'));
  }, [dispatch]);

  useEffect(() => {
    const medsWithVideos = medicationsFromPrescriptions.filter(
      (medication: Medication) => !!getInstructionalVideosForMedication(medication).length,
    );

    trackPageView({
      event: EVENTS.MED_LIST_VIEWED,
      params: {
        eligibleForNudgeCardExperiment: undefined,
        'has videos': medsWithVideos?.length > 0,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // open onboarding modal
  useEffect(() => {
    if (!onboarding) return;
    dispatch(openModal('ONBOARDING_MODAL'));
    dispatch(changeModalStep(0, 'ONBOARDING_MODAL'));
  }, [onboarding, dispatch]);

  const onSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  if (prescriptions.length > 0 || transfers.length > 0) {
    return (
      <PageBackground>
        <ContentWrapper>
          <Main>
            <HeaderRow>
              <PageHeader announceToScreenReader>Meds</PageHeader>
              <MedSearch
                icon={
                  <FontAwesomeIcon
                    color={palette.grey1}
                    icon={faSearch}
                  />
                }
                input={{
                  onChange: onSearchChange,
                  value: searchTerm,
                }}
                placeholder="Search for medications"
              />
            </HeaderRow>

            {searchTerm ? <MedListSearchResults searchTerm={searchTerm} /> : <MedList initialRxNumber={rxNumber} />}
            <SmPadding>
              <Row
                center
                wrap
              >
                <CenteredText
                  small
                  light
                >
                  Why can’t I see my prescription?
                </CenteredText>
                <XxsSpacing />
                <PressableAltoIcon
                  name="info-small"
                  onPress={openFAQs}
                  accessibilityLabel="Press to view frequently asked questions"
                />
              </Row>
            </SmPadding>
          </Main>
          <Sidebar />
        </ContentWrapper>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <MyMedsEmpty
        user={user}
        openSendPrescriptionOptions={openSendPrescriptionOptions}
        openFAQs={openFAQs}
      />
    </PageBackground>
  );
};
